import type { AnalyticsEmissionTypeToggle } from './default-market-based-utils';

/**
 * Analytics dashboard view state <-> query string.
 *
 * Canonical params, written by the page's serialiser:
 *   tab     = overview | energy | emissions | surveys | flag
 *   dim     = group | subsidiary | location | supplier | project (only meaningful on the Emissions tab)
 *   basis   = location | market (same vocabulary as the Emissions Breakdown page)
 *   compare = report period id for the "Compare to" selector. Absent means automatic (the page
 *             derives the previous period itself, exactly as before the selector existed), so the
 *             serialiser writes it only when a period was explicitly chosen. Not validated here:
 *             report periods load asynchronously, so the page resolves an id that does not match
 *             one of the organisation's periods back to automatic instead of dropping it.
 *
 * Legacy params, still resolved so old links keep working:
 *   tab   = group | subsidiary | location | supplier | project: the per-dimension tab names from
 *           when each dimension was its own tab. Saved home views still produce these.
 *   tabId = numeric index into the old tab order, which depended on org feature flags:
 *           Overview, Energy, Group, [Subsidiary], Location, Supplier, [Project], Surveys, [FLAG].
 *   view  = flag: the /analytics/dashboard/flag route alias.
 */

export type AnalyticsTabKey = 'overview' | 'energy' | 'emissions' | 'surveys' | 'flag';
export type AnalyticsEmissionsDimension = 'group' | 'subsidiary' | 'location' | 'supplier' | 'project';

export const ANALYTICS_TAB_KEYS: AnalyticsTabKey[] = ['overview', 'energy', 'emissions', 'surveys', 'flag'];
export const ANALYTICS_EMISSIONS_DIMENSIONS: AnalyticsEmissionsDimension[] = [
  'group', 'subsidiary', 'location', 'supplier', 'project',
];

export interface IAnalyticsOrgCapabilities {
  hasSubsidiaries?: boolean;
  hasProjects?: boolean;
  flagEnabled?: boolean;
}

export interface IAnalyticsViewParams {
  tab: AnalyticsTabKey;
  dimension: AnalyticsEmissionsDimension;
  basis: AnalyticsEmissionTypeToggle;
  /** Report period id to compare against; undefined = automatic previous period. */
  compare?: string;
}

interface IResolvedTab {
  tab: AnalyticsTabKey;
  dimension?: AnalyticsEmissionsDimension;
}

/** The tab order the page had before the five "Emissions by X" tabs were collapsed into one. */
const legacyTabOrder = (capabilities: IAnalyticsOrgCapabilities): string[] => [
  'overview',
  'energy',
  'group',
  ...(capabilities.hasSubsidiaries ? ['subsidiary'] : []),
  'location',
  'supplier',
  ...(capabilities.hasProjects ? ['project'] : []),
  'surveys',
  ...(capabilities.flagEnabled ? ['flag'] : []),
];

/** Resolves a canonical tab key, or a legacy per-dimension tab name, to the view it means now. */
const resolveTabKey = (key: string): IResolvedTab | undefined => {
  if ((ANALYTICS_TAB_KEYS as string[]).includes(key)) return { tab: key as AnalyticsTabKey };
  if ((ANALYTICS_EMISSIONS_DIMENSIONS as string[]).includes(key)) {
    return { tab: 'emissions', dimension: key as AnalyticsEmissionsDimension };
  }
  return undefined;
};

export const parseAnalyticsViewParams = (
  search: URLSearchParams,
  capabilities: IAnalyticsOrgCapabilities,
): Partial<IAnalyticsViewParams> => {
  const parsed: Partial<IAnalyticsViewParams> = {};

  const basis = search.get('basis');
  if (basis === 'market') parsed.basis = 'marketBased';
  if (basis === 'location') parsed.basis = 'locationBased';

  const compare = search.get('compare');
  if (compare) parsed.compare = compare;

  const dim = search.get('dim');
  if (dim && (ANALYTICS_EMISSIONS_DIMENSIONS as string[]).includes(dim)) {
    parsed.dimension = dim as AnalyticsEmissionsDimension;
  }

  const tab = search.get('tab');
  if (tab) {
    const resolved = resolveTabKey(tab);
    if (resolved) {
      parsed.tab = resolved.tab;
      if (resolved.dimension && !parsed.dimension) parsed.dimension = resolved.dimension;
    }
  }

  if (!parsed.tab && search.get('view') === 'flag') parsed.tab = 'flag';

  const tabId = search.get('tabId');
  if (!parsed.tab && tabId !== null) {
    const index = parseInt(tabId, 10);
    const legacyKey = Number.isNaN(index) ? undefined : legacyTabOrder(capabilities)[index];
    const resolved = legacyKey ? resolveTabKey(legacyKey) : undefined;
    if (resolved) {
      parsed.tab = resolved.tab;
      if (resolved.dimension && !parsed.dimension) parsed.dimension = resolved.dimension;
    } else {
      // Out-of-range index (stale link from an org that since lost a feature): land on Overview.
      parsed.tab = 'overview';
    }
  }

  // Drop anything this organisation cannot show.
  if (parsed.tab === 'flag' && !capabilities.flagEnabled) parsed.tab = 'overview';
  if (parsed.dimension === 'subsidiary' && !capabilities.hasSubsidiaries) parsed.dimension = 'group';
  if (parsed.dimension === 'project' && !capabilities.hasProjects) parsed.dimension = 'group';

  return parsed;
};

export const serialiseAnalyticsViewParams = (view: IAnalyticsViewParams): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('tab', view.tab);
  if (view.tab === 'emissions') params.set('dim', view.dimension);
  params.set('basis', view.basis === 'marketBased' ? 'market' : 'location');
  // Automatic comparison is the absence of the param, so hydration must treat a missing compare
  // as "clear back to automatic" (unlike tab/dim/basis, which are always present once canonical).
  if (view.compare) params.set('compare', view.compare);
  return params;
};
